import { api } from '@agency/shared';
import { Button, Card, Chip, DataTable, EmptyState, KeyValue, MetricCell, MetricRow, PageHeader, Skeleton, StatusDot, Timeline, fmtCount, fmtMicroUsd, fmtRelative, fmtTimestamp, type DataTableColumn, type TimelineItem } from '@agency/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { ErrorCallout, FooterMeta, Grid, Page, Stamp, UserLink } from '../app/widgets.js';
import { OUTCOME_TONE, ROLLOUT_LABEL, ROLLOUT_TONE, pluralize } from '../lib/labels.js';
import { useMutation } from '../lib/mutate.js';
import { useSession } from '../lib/session.js';
import { usageCostCell } from '../lib/usageCost.js';
import { useApi } from '../lib/useApi.js';

type OutboxRow = api.adminOps.OutboxItem;

export function OverviewPage() {
  const session = useSession();
  const navigate = useNavigate();
  const mutation = useMutation();
  const overview = useApi('admin.overview', {}, { refreshMs: 60_000 });
  const canPeople = session.has('people.manage');
  const canAudit = session.has('audit.read');
  const canOps = session.has(['device.manage', 'audit.read']);
  const canUsage = session.has(['audit.read', 'ai_key.manage']);
  const canTemplates = session.has(['template.edit', 'template.publish']);
  const canAccounts = session.has(['account.assign', 'account.register', 'audit.read']);
  // local-accounts change: unique accounts are registry rows; operators and devices come from presence reports.
  const accountStats = useApi('admin.accounts.stats', {}, { enabled: canAccounts });

  const invitations = useApi('admin.invitations.list', { input: { status: 'pending', limit: 10 } }, { enabled: canPeople });
  const accessChanges = useApi('admin.audit.query', { input: { family: 'access', limit: 8 } }, { enabled: canAudit });
  const outbox = useApi('admin.ops.outbox.list', { input: { state: 'dead', limit: 20 } }, { enabled: canOps });
  const outboxFailed = useApi('admin.ops.outbox.list', { input: { state: 'failed', limit: 20 } }, { enabled: canOps });
  const denied = useApi('admin.audit.query', { input: { outcome: 'denied', limit: 10 } }, { enabled: canAudit });
  const since7d = useMemo(() => new Date(Date.now() - 7 * 86_400_000).toISOString(), []);
  const usage = useApi('admin.usage.summary', { input: { from: since7d } }, { enabled: canUsage });
  const delivery = useApi('admin.audit.delivery', {}, { enabled: canAudit });
  const policy = useApi('admin.ops.releasePolicy.get', {}, { enabled: canOps });
  const releases = useApi('admin.content.releases.list', { input: { limit: 5 } }, { enabled: canTemplates });

  const o = overview.data;
  const a = accountStats.data;
  const subtitle = o
    ? `${pluralize(o.access.activeUsers, 'active user')} · ${pluralize(o.access.activeDevices, 'active device')}${a ? ` · ${pluralize(a.uniqueCustomers, 'account')}` : ''} · ${o.access.devicesNeedingUpdate} need update · ${o.auditDelivery.staleReporters} stale reporters · ${o.failures.outboxDead} dead outbox items`
    : 'Loading counts.';

  const failureRows = useMemo<OutboxRow[]>(() => [...(outbox.data?.items ?? []), ...(outboxFailed.data?.items ?? [])], [outbox.data, outboxFailed.data]);
  const failureColumns = useMemo<DataTableColumn<OutboxRow>[]>(
    () => [
      { id: 'kind', header: 'Kind', accessorKey: 'kind', cell: ({ row }) => <span className="font-mono text-xs-plus uppercase text-mute">{row.original.kind.replace(/_/g, ' ')}</span>, size: 150 },
      { id: 'state', header: 'State', cell: ({ row }) => <Chip tone={row.original.state === 'dead' ? 'brand' : 'warn'}>{row.original.state}</Chip>, size: 90 },
      { id: 'attempts', header: 'Attempts', cell: ({ row }) => <span className="font-mono tabular-nums">{row.original.attempts}/{row.original.maxAttempts}</span>, size: 90 },
      { id: 'error', header: 'Last error', cell: ({ row }) => <span className="truncate text-mute" title={row.original.lastError ?? ''}>{row.original.lastError ?? '--'}</span> },
      { id: 'next', header: 'Next attempt', cell: ({ row }) => <Stamp value={row.original.nextAttemptAt} relative />, size: 120 },
      {
        id: 'actions',
        header: '',
        size: 90,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="tertiary"
            icon={RefreshCw}
            disabled={!session.has('device.manage')}
            onClick={() => void mutation.run('admin.ops.outbox.retry', { params: { itemId: row.original.id }, input: {} }, { title: 'Outbox item rescheduled', invalidate: ['admin.ops.outbox', 'admin.overview'] })}
          >
            Retry
          </Button>
        ),
      },
    ],
    [mutation, session],
  );

  const accessTimeline = useMemo<TimelineItem[]>(
    () =>
      (accessChanges.data?.items ?? []).map((e) => ({
        id: e.eventId,
        meta: [fmtTimestamp(e.occurredAt), e.action.replace('access.', '').replace(/_/g, ' ')],
        delta: e.outcome,
        deltaTone: e.outcome === 'success' ? 'ok' : e.outcome === 'denied' ? 'brand' : 'warn',
        title: <Link to="/audit/$eventId" params={{ eventId: e.eventId }} className="hover:underline">{e.action}</Link>,
        summary: <UserLink userId={e.actorUserId} />,
        nodeTone: e.outcome === 'success' ? 'ok' : 'brand',
      })),
    [accessChanges.data],
  );

  const u = usage.data;
  // `--` labelled "No usage" for an empty window and for an unknown estimate; never $0 for unknown.
  const cost = u ? usageCostCell(u.summary, { hasUsage: u.hasUsage, format: fmtMicroUsd }) : null;

  return (
    <Page>
      <PageHeader
        eyebrow="Agency"
        eyebrowPath="Administration / Overview"
        title="Agency administration"
        subtitle={subtitle}
        actions={
          <Button variant="primary" icon={RefreshCw} loading={overview.refreshing} onClick={() => void overview.reload()}>
            Refresh
          </Button>
        }
      />
      <ErrorCallout error={overview.error} onRetry={() => void overview.reload()} className="mt-6" />
      <Grid>
        <Card title="Access" subtitle={o ? `${o.access.activeSessions} active sessions` : 'Loading'} span="col-span-12 lg:col-span-4">
          {o ? (
            <MetricRow columns={3}>
              <MetricCell label="Active" value={fmtCount(o.access.activeUsers)} />
              <MetricCell label="Invited" value={fmtCount(o.access.invitedUsers)} tone={o.access.invitedUsers > 0 ? 'info' : 'ink'} />
              <MetricCell label="Suspended" value={fmtCount(o.access.suspendedUsers)} tone={o.access.suspendedUsers > 0 ? 'warn' : 'ink'} />
            </MetricRow>
          ) : (
            <Skeleton height={56} />
          )}
          <p className="label-mono mt-5">Account registry</p>
          {!canAccounts ? (
            <p className="mt-2 text-xs text-mute">Requires account.register, account.assign or audit.read.</p>
          ) : a ? (
            <>
              <MetricRow columns={3} className="mt-2">
                <MetricCell label="Accounts" value={fmtCount(a.uniqueCustomers)} />
                <MetricCell label="Operators" value={fmtCount(a.operators)} />
                <MetricCell label="Devices" value={fmtCount(a.devicesReporting)} />
              </MetricRow>
              <p className="mt-1 text-xs text-mute">Unique accounts are registry rows; operators and devices are counted from presence reports. {a.reportedAccounts} of {a.uniqueCustomers} accounts are present on at least one device.</p>
            </>
          ) : accountStats.error ? (
            <ErrorCallout error={accountStats.error} onRetry={() => void accountStats.reload()} className="mt-2" />
          ) : (
            <Skeleton height={56} className="mt-2" />
          )}
          <p className="label-mono mt-5">Pending invitations</p>
          {!canPeople ? (
            <p className="mt-2 text-xs text-mute">Requires people.manage.</p>
          ) : invitations.loading ? (
            <Skeleton height={40} className="mt-2" />
          ) : (invitations.data?.items.length ?? 0) === 0 ? (
            <EmptyState compact message="No pending invitations." />
          ) : (
            <ul className="mt-2 divide-y divide-hair">
              {invitations.data?.items.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between gap-3 py-2 text-[12.5px]">
                  <div className="min-w-0">
                    <p className="truncate">{inv.displayName ?? inv.email}</p>
                    <p className="font-mono text-2xs text-mute">expires {fmtRelative(inv.expiresAt)} · {inv.deliveryState ?? 'not sent'}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" variant="ghost" trailingArrow={false} onClick={() => void mutation.run('admin.invitations.resend', { params: { invitationId: inv.id }, input: {} }, { title: 'Invitation resent', invalidate: ['admin.invitations', 'admin.overview'] })}>Resend</Button>
                    <Button size="sm" variant="ghost" trailingArrow={false} onClick={() => void mutation.run('admin.invitations.cancel', { params: { invitationId: inv.id }, input: {} }, { title: 'Invitation cancelled', invalidate: ['admin.invitations', 'admin.overview', 'admin.people'] })}>Cancel</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="label-mono mt-5">Recent access changes</p>
          {!canAudit ? <p className="mt-2 text-xs text-mute">Requires audit.read.</p> : accessChanges.loading ? <Skeleton height={80} className="mt-2" /> : accessTimeline.length === 0 ? <EmptyState compact message="No access changes recorded." /> : <Timeline items={accessTimeline} className="mt-2" />}
        </Card>

        <Card
          title="Failures"
          subtitle={o ? `${o.failures.outboxDead} dead · ${o.failures.mailFailures} mail failed · ${o.failures.deniedActions24h} denied 24h · ${o.failures.providerRevocationsPending} revocations pending` : 'Loading'}
          span="col-span-12 lg:col-span-8"
          flush
          rightSlot={<Button variant="ghost" onClick={() => void navigate({ to: '/ops/outbox' })}>Open outbox</Button>}
        >
          {!canOps ? (
            <EmptyState message="Requires device.manage or audit.read." />
          ) : (
            <DataTable<OutboxRow>
              aria-label="Operational failures"
              columns={failureColumns}
              rows={failureRows}
              rowKey={(r) => r.id}
              height={Math.min(300, Math.max(120, failureRows.length * 34 + 40))}
              state={outbox.loading ? 'loading' : failureRows.length === 0 ? 'empty' : 'complete'}
              emptyMessage="No failed or dead outbox items."
              noun="items"
              isCritical={(r) => r.state === 'dead'}
            />
          )}
          <div className="border-t border-hair px-5 py-3">
            <p className="label-mono">Denied actions</p>
            {!canAudit ? (
              <p className="mt-1 text-xs text-mute">Requires audit.read.</p>
            ) : (denied.data?.items.length ?? 0) === 0 ? (
              <p className="mt-1 text-xs text-mute">No denied actions recorded.</p>
            ) : (
              <ul className="mt-1 flex flex-wrap gap-2">
                {denied.data?.items.slice(0, 6).map((e) => (
                  <li key={e.eventId}>
                    <Link to="/audit/$eventId" params={{ eventId: e.eventId }} className="inline-flex items-center gap-2 rounded-sm border border-line px-2 py-1 text-xs hover:bg-card">
                      <Chip tone={OUTCOME_TONE[e.outcome]}>{e.outcome}</Chip>
                      <span className="font-mono text-2xs text-mute">{e.action}</span>
                      <span className="font-mono text-2xs text-dim">{fmtRelative(e.occurredAt)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card title="Usage" subtitle="Last 7 days · device reported" span="col-span-12 lg:col-span-6" rightSlot={<Button variant="ghost" onClick={() => void navigate({ to: '/usage' })}>Usage</Button>}>
          {!canUsage ? (
            <EmptyState compact message="Requires audit.read or ai_key.manage." />
          ) : usage.loading ? (
            <Skeleton height={120} />
          ) : u ? (
            <>
              <MetricRow columns={3}>
                <MetricCell label="Rumi requests" value={fmtCount(u.activity.rumiRequests)} />
                <MetricCell label="Tokens" value={fmtCount(u.summary.inputTokens + u.summary.outputTokens)} />
                {cost ? <MetricCell label={cost.label} value={cost.value} tone={cost.tone} unit={cost.unit} /> : null}
              </MetricRow>
              <MetricRow columns={4} className="mt-3">
                <MetricCell label="Manual syncs" value={`${fmtCount(u.activity.syncCompletions)}`} unit={`/${fmtCount(u.activity.syncAttempts)}`} />
                <MetricCell label="429 waits" value={fmtCount(u.activity.rateLimitWaits)} tone={u.activity.rateLimitWaits > 0 ? 'warn' : 'ink'} />
                <MetricCell label="Failed datasets" value={fmtCount(u.activity.failedDatasets)} tone={u.activity.failedDatasets > 0 ? 'brand' : 'ink'} />
                <MetricCell label="Drafts / sends" value={`${fmtCount(u.activity.gmailDrafts)} / ${fmtCount(u.activity.gmailSends)}`} />
              </MetricRow>
              <p className="mt-3 font-mono text-2xs uppercase tracking-[0.18em] text-mute">
                {u.summary.unknownCount > 0 ? `${u.summary.unknownCount} records with unknown cost · ` : ''}
                reconciliation {u.reconciliation.lastResult}{u.reconciliation.lastRunAt ? ` · ${fmtRelative(u.reconciliation.lastRunAt)}` : ''}
              </p>
            </>
          ) : (
            <ErrorCallout error={usage.error} onRetry={() => void usage.reload()} />
          )}
        </Card>

        <Card title="Audit delivery" subtitle={delivery.data ? `${delivery.data.eventsLast24h} events in 24h · stale after ${delivery.data.staleThresholdHours}h` : 'Loading'} span="col-span-12 lg:col-span-6" rightSlot={<Button variant="ghost" onClick={() => void navigate({ to: '/audit' })}>Audit</Button>}>
          {!canAudit ? (
            <EmptyState compact message="Requires audit.read." />
          ) : delivery.loading ? (
            <Skeleton height={120} />
          ) : delivery.data ? (
            <>
              <MetricRow columns={3}>
                <MetricCell label="Reporters" value={fmtCount(delivery.data.reporters.length)} />
                <MetricCell label="Unacknowledged" value={fmtCount(delivery.data.reporters.reduce((n, r) => n + r.unacknowledged, 0))} />
                <MetricCell label="Stale" value={fmtCount(delivery.data.reporters.filter((r) => r.stale).length)} tone={delivery.data.reporters.some((r) => r.stale) ? 'warn' : 'ink'} />
              </MetricRow>
              {delivery.data.reporters.length === 0 ? (
                <EmptyState compact message="No device has reported audit batches yet." />
              ) : (
                <table className="mt-3 w-full text-[12.5px]">
                  <thead>
                    <tr className="label-mono border-b border-hair text-left">
                      <th className="py-1.5 font-normal">Device</th>
                      <th className="py-1.5 font-normal">Last received</th>
                      <th className="py-1.5 text-right font-normal">Seq</th>
                      <th className="py-1.5 text-right font-normal">Gaps</th>
                    </tr>
                  </thead>
                  <tbody>
                    {delivery.data.reporters.slice(0, 6).map((r) => (
                      <tr key={r.deviceId} className="border-b border-hair">
                        <td className="py-1.5">
                          <StatusDot tone={r.stale ? 'warn' : 'ok'} mono={false} label={<span>{r.deviceName} <span className="text-mute">· {r.userDisplayName}</span></span>} />
                        </td>
                        <td className="py-1.5"><Stamp value={r.lastReceivedAt} relative /></td>
                        <td className="py-1.5 text-right font-mono tabular-nums">{r.lastSequence ?? '--'}</td>
                        <td className="py-1.5 text-right font-mono tabular-nums">{r.gaps.length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="mt-3 text-xs text-mute">An offline device is not evidence that no work occurred.</p>
            </>
          ) : (
            <ErrorCallout error={delivery.error} onRetry={() => void delivery.reload()} />
          )}
        </Card>

        <Card title="Releases" subtitle="Desktop policy and content bundle" span="col-span-12">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-6">
              <p className="label-mono">Desktop</p>
              {!canOps ? (
                <p className="mt-2 text-xs text-mute">Requires device.manage or audit.read.</p>
              ) : policy.data ? (
                <KeyValue
                  className="mt-2"
                  items={[
                    { key: 'Current version', value: policy.data.policy.currentAppVersion, mono: true },
                    { key: 'Minimum version', value: policy.data.policy.minAppVersion, mono: true },
                    { key: 'Stage', value: <Chip tone={ROLLOUT_TONE[policy.data.policy.stage]}>{ROLLOUT_LABEL[policy.data.policy.stage]}</Chip> },
                    { key: 'Cohorts', value: policy.data.policy.cohorts.map((c) => `${c.name} ${c.userIds.length}`).join(' · ') || '--', mono: true },
                    { key: 'Approved builds', value: String(policy.data.policy.approvedBuildIds.length), mono: true },
                  ]}
                />
              ) : (
                <Skeleton height={100} className="mt-2" />
              )}
            </div>
            <div className="col-span-12 lg:col-span-6">
              <p className="label-mono">Content bundle</p>
              {o ? (
                <KeyValue
                  className="mt-2"
                  items={[
                    { key: 'Active version', value: o.content.activeVersion ?? '--', mono: true },
                    { key: 'Rollout', value: o.content.rolloutStage ?? '--', mono: true },
                    { key: 'Adoptions', value: String(o.content.adoptionCount), mono: true },
                    { key: 'Latest release', value: releases.data?.items[0] ? `${releases.data.items[0].manifest.version} · ${releases.data.items[0].manifest.channel} · ${fmtRelative(releases.data.items[0].createdAt)}` : '--', mono: true },
                  ]}
                />
              ) : (
                <Skeleton height={100} className="mt-2" />
              )}
            </div>
          </div>
        </Card>
      </Grid>
      <FooterMeta left={`Agency · ${session.session?.mode ?? '--'} mode`} right={o ? `Generated ${fmtTimestamp(o.generatedAt)}` : ''} />
    </Page>
  );
}
